import { useRouter } from 'next/router';
import { FC, useEffect } from 'react';
import { TbStarFilled } from 'react-icons/tb';

import { Button } from '@mantine/core';

import {
    CompanyProfile,
    CompanySubscription,
    PermissionCompany,
    PublishedStatus,
    SubscriptionBillingCycle,
    SubscriptionType,
} from 'models';
import { CompanyHelpers } from 'helpers/CompanyHelpers';
import { MarketingHelpers } from 'helpers/MarketingHelpers';
import { SubscriptionHelpers } from 'helpers/SubscriptionHelpers';

import { ProfileNotice } from 'components/company-profile/components/ProfileNotice';
import { useProfilePermission } from 'components/company-profile/hooks/use-profile-permission';
import { useFreeLeads } from 'hooks/use-free-leads';
import { useTeam } from 'hooks/use-team';
import { useWorkspaceConfig } from 'hooks/use-workspace-config';
import { SubscriptionModalService } from 'services/SubscriptionModalService';

const CompanySubscriptionNotice: FC<{ company: CompanyProfile }> = ({ company }) => {
    const router = useRouter();
    const { team } = useTeam(company.team);
    const canEdit = useProfilePermission(PermissionCompany.EDIT, company);

    const { workspace: inWorkspace } = useWorkspaceConfig();

    const subscription = SubscriptionHelpers.getSubscriptionData(
        SubscriptionType.COMPANY,
        team?.subscriptions,
    ).subscription;

    const showUpgrade = Boolean(
        team &&
            canEdit &&
            !inWorkspace &&
            subscription !== CompanySubscription.PREMIUM &&
            !(company.status === PublishedStatus.DRAFT && subscription === CompanySubscription.NONE),
    );

    const { freeLeads, isLoading: freeLeadsLoading } = useFreeLeads(showUpgrade ? company.id : undefined);
    const message =
        freeLeads && !freeLeads.unlimited && freeLeads.used >= freeLeads.limit
            ? `You've used your ${freeLeads.limit} free leads. Upgrade to see everyone who reaches out.`
            : 'Want more leads? Premium boosts your profile in search.';

    const openUpgradeModal = () => {
        if (!team) return;

        SubscriptionModalService.open({
            type: SubscriptionType.COMPANY,
            toSubscription: CompanySubscription.PREMIUM,
            team,
            redirectUrl: CompanyHelpers.urls.view(company.slug),
            defaultBillingCycle: SubscriptionBillingCycle.YEARLY,
        });
    };

    useEffect(() => {
        const { action, ...updatedQuery } = router.query;

        if (action === 'upgrade' && showUpgrade) {
            openUpgradeModal();

            router.replace({ pathname: router.pathname, query: updatedQuery }, undefined, { shallow: true }).then();
        }
    }, [router, showUpgrade]);

    if (!team || !showUpgrade || freeLeadsLoading) {
        return null;
    }

    return (
        <ProfileNotice
            title="Upgrade your profile to Premium"
            action={
                <Button
                    component="a"
                    href={MarketingHelpers.urls.resourcepro.pricing(CompanyHelpers.urls.upgrade(company.slug))}
                    variant="white"
                    size="sm"
                    radius="xs"
                    leftSection={<TbStarFilled size={14} color="#ffd43b" />}
                >
                    Upgrade now
                </Button>
            }
        >
            {message}
        </ProfileNotice>
    );
};

export { CompanySubscriptionNotice };
